import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  List,
  ListItem,
  Stack,
  Typography,
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

const SUPPORTED_COUNTRIES = [
  'United States',
  'Albania',
  'Algeria',
  'American Samoa',
  'Andorra',
  'Angola',
  'Anguilla',
  'Antarctica',
  'Antigua and Barbuda',
  'Argentina',
  'Armenia',
  'Aruba',
  'Australia',
  'Austria',
  'Azerbaijan',
  'Bahamas',
  'Bahrain',
  'Bangladesh',
  'Barbados',
  'Belgium',
  'Belize',
  'Benin',
  'Bermuda',
  'Bhutan',
  'Bolivia',
  'Bonaire',
  'Bosnia and Herzegovina',
  'Botswana',
  'Bouvet Island',
  'Brazil',
  'British Indian Ocean Territory',
  'British Virgin Islands',
  'Brunei Darussalam',
  'Bulgaria',
  'Burkina Faso',
  'Burundi',
  'Cambodia',
  'Cameroon',
  'Canada',
  'Cape Verde',
  'Cayman Islands',
  'Chad',
  'Chile',
  'Christmas Island',
  'Cocos (Keeling) Islands',
  'Colombia',
  'Comoros',
  'Congo, Republic of the',
  'Cook Islands',
  'Costa Rica',
  "Cote d'Ivoire",
  'Croatia',
  'Curacao',
  'Cyprus',
  'Czech Republic',
  'Denmark',
  'Djibouti',
  'Dominica',
  'Dominican Republic',
  'East Timor',
  'Ecuador',
  'Egypt',
  'El Salvador',
  'Equatorial Guinea',
  'Eritrea',
  'Estonia',
  'Eswatini',
  'Falkland Islands (Malvinas)',
  'Faroe Islands',
  'Fiji',
  'Finland',
  'France',
  'French Guiana',
  'French Polynesia',
  'French Southern Territories',
  'Gabon',
  'Gambia',
  'Georgia',
  'Germany',
  'Ghana',
  'Gibraltar',
  'Greece',
  'Greenland',
  'Grenada',
  'Guadeloupe',
  'Guam',
  'Guatemala',
  'Guernsey',
  'Guinea',
  'Guinea-Bissau',
  'Guyana',
  'Haiti',
  'Heard Island and McDonald Islands',
  'Holy See (Vatican City State)',
  'Honduras',
  'Hungary',
  'Iceland',
  'Ireland',
  'Isle of Man',
  'Israel',
  'Italy',
  'Jamaica',
  'Japan',
  'Jersey',
  'Jordan',
  'Kazakhstan',
  'Kenya',
  'Kiribati',
  'Kuwait',
  'Kyrgyzstan',
  "Lao People's Democratic Republic",
  'Latvia',
  'Lesotho',
  'Liberia',
  'Liechtenstein',
  'Lithuania',
  'Luxembourg',
  'Macao',
  'Macedonia, the Former Yugoslav Republic of',
  'Madagascar',
  'Malawi',
  'Maldives',
  'Malta',
  'Marshall Islands',
  'Martinique',
  'Mauritania',
  'Mauritius',
  'Mayotte',
  'Mexico',
  'Micronesia, Federated States of',
  'Moldova, Republic of',
  'Monaco',
  'Mongolia',
  'Montenegro',
  'Montserrat',
  'Morocco',
  'Mozambique',
  'Namibia',
  'Nauru',
  'Nepal',
  'Netherlands',
  'New Caledonia',
  'New Zealand',
  'Niger',
  'Nigeria',
  'Niue',
  'Norfolk Island',
  'Northern Mariana Islands',
  'Norway',
  'Oman',
  'Pakistan',
  'Palau',
  'Palestine, State of',
  'Panama',
  'Paraguay',
  'Peru',
  'Pitcairn',
  'Poland',
  'Portugal',
  'Puerto Rico',
  'Qatar',
  'Reunion',
  'Romania',
  'Rwanda',
  'Saint Barthelemy',
  'Saint Helena',
  'Saint Kitts and Nevis',
  'Saint Lucia',
  'Saint Martin (French part)',
  'Saint Pierre and Miquelon',
  'Saint Vincent and the Grenadines',
  'Samoa',
  'San Marino',
  'Sao Tome and Principe',
  'Saudi Arabia',
  'Senegal',
  'Serbia',
  'Seychelles',
  'Sierra Leone',
  'Singapore',
  'Sint Maarten (Dutch part)',
  'Slovakia',
  'Slovenia',
  'Solomon Islands',
  'South Africa',
  'South Georgia and the South Sandwich Islands',
  'South Korea',
  'Spain',
  'Sri Lanka',
  'Suriname',
  'Svalbard and Jan Mayen',
  'Sweden',
  'Switzerland',
  'Taiwan, Province of China',
  'Tajikistan',
  'Togo',
  'Tokelau',
  'Tonga',
  'Trinidad and Tobago',
  'Tunisia',
  'Turkey',
  'Turkmenistan',
  'Turks and Caicos Islands',
  'Tuvalu',
  'United Arab Emirates',
  'United Kingdom',
  'United Republic of Tanzania',
  'United States Minor Outlying Islands',
  'Uruguay',
  'US Virgin Islands',
  'Uzbekistan',
  'Vietnam',
  'Wallis and Futuna',
  'Western Sahara',
  'Zambia',
];

export const ReferFriendConsentContent = (): JSX.Element => (
  <Stack spacing={3}>
    <Typography variant="body1" component="p" color="secondary">
      Last updated: February, 2026
    </Typography>
    <Typography variant="body1" component="p">
      These External Referral Program Terms and Conditions ("Terms") constitute
      a legally binding agreement governing participation in the External
      Referral Program (the "Program") operated by AgileEngine, LLC.
      ("Company"). These Terms shall also apply to, and be enforceable by, any
      parent company, subsidiary, or other entity directly or indirectly
      controlling, controlled by, or under common control with the Company
      (collectively, "Affiliates").
    </Typography>
    <Typography variant="body1" component="p">
      By submitting a referral or otherwise participating in the Program, the
      individual ("Referrer") acknowledges that they have read, understood, and
      agreed to be bound by these Terms.
    </Typography>
    <Typography variant="h4">1. Program overview</Typography>
    <Typography variant="body1" component="p">
      The Program permits eligible external individuals to refer qualified
      candidates for open positions with the Company or its Affiliates. Referral
      commissions or bonuses may be awarded if a referred candidate is hired and
      all conditions set forth in these Terms are fully satisfied. Participation
      in the Program is voluntary and does not guarantee compensation.
    </Typography>
    <Typography variant="h4">2. Eligible referrers</Typography>
    <Typography variant="body1" component="p">
      Referrers must be legally resident in, and physically located within, one
      of the following countries:
    </Typography>
    <Box mt={2} mb={3}>
      <Accordion>
        <AccordionSummary
          expandIcon={<ArrowDownwardIcon />}
          sx={(theme) => ({
            bgcolor: theme.palette.highlight.accent,
          })}
        >
          <Typography variant="body1" mb={0.5} ml={1.75}>
            Supported countries
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <List dense>
            {SUPPORTED_COUNTRIES.map((country) => (
              <ListItem key={country} sx={{ py: 0, my: -1 }}>
                <Typography variant="body1" component="span">
                  {country}
                </Typography>
              </ListItem>
            ))}
          </List>
        </AccordionDetails>
      </Accordion>
    </Box>
    <Typography variant="body1" component="p">
      Individuals who are not located in such countries may be ineligible to
      redeem the applicable commissions, even if they have otherwise complied
      with all terms and conditions hereof.
    </Typography>
    <Typography variant="body1" component="p">
      The Program is open to individual external participants who submit
      referrals through the Company's designated referral form. Current
      employers/contractors of the Company or its Affiliates are not eligible to
      participate.
    </Typography>
    <Typography variant="body1" component="p">
      The Company reserves the right to exclude any participant who, at the
      Company's full discretion, acts fraudulently, pretends to take an unfair
      advantage from this program, or acts against the interests of the Company
      and/or this program.
    </Typography>
    <Typography variant="h4">3. Submission of referrals</Typography>
    <Typography variant="body1" component="p">
      Referrals must be submitted exclusively through the Company's official
      referral form and channels. Required information includes Referrer details
      (name, email, country) and candidate details (name, email, country, etc.).
      Referrals submitted through other channels shall be deemed invalid.
    </Typography>
    <Typography variant="h4">4. Review and discretion</Typography>
    <Typography variant="body1" component="p">
      All referrals are subject to review by the Company or its Affiliates. A
      referral may be rejected if the candidate is already in an active
      recruitment process, was referred within the previous twelve (12) months,
      or does not meet current hiring needs. All determinations are made at the
      sole discretion of the Company.
    </Typography>
    <Typography variant="h4">5. Referral commissions</Typography>
    <Typography variant="body1" component="p">
      Referral commission amounts vary by role seniority and candidate location,
      as described below:
    </Typography>
    <Stack spacing={1}>
      <Typography variant="body1" component="p">
        <b>Non-United States roles:</b>
      </Typography>
      <List>
        <ListItem>Non-engineering & Junior experts: USD 500</ListItem>
        <ListItem>Middle experts: USD 700</ListItem>
        <ListItem>Senior & Lead experts: USD 1,000</ListItem>
      </List>
      <Typography variant="body1" component="p">
        <b>United States roles:</b>
      </Typography>
      <List>
        <ListItem>Non-engineering & Junior experts: USD 1,000</ListItem>
        <ListItem>Middle experts: USD 1,500</ListItem>
        <ListItem>Senior & Lead experts: USD 2,500</ListItem>
      </List>
    </Stack>
    <Typography variant="body1" component="p">
      Eligible Locations for referrals: Argentina, Brazil, Colombia, Guatemala,
      Mexico, Poland, Portugal, Bulgaria, Slovenia, Slovakia, Spain, and the
      United States.
    </Typography>
    <Typography variant="body1" component="p">
      The Company reserves the right to modify commission structures at any time
      prior to approval. Locations not listed above will not be eligible for any
      commissions.
    </Typography>
    <Typography variant="h4">6. Eligibility and payment conditions</Typography>
    <Typography variant="body1" component="p">
      Referral commissions shall be payable only if all Program requirements are
      satisfied, including that the candidate is hired within 180 days of
      referral and completes three (3) consecutive months of work under any
      arrangement approved by the Company. Commissions may be paid directly by
      the Company or by the respective hiring Affiliate.
    </Typography>
    <Typography variant="h4">7. Payment method and timeline</Typography>
    <Typography variant="body1" component="p">
      Approved commissions shall be paid within forty-five (45) days after
      eligibility is confirmed, via VISA electronic prepaid card or other method
      designated by the Company or its Affiliates.
    </Typography>
    <Typography variant="body1" component="p">
      Payments are subject to the compliance of all applicable laws and
      regulations. If the payment of commissions under this program is subject
      to legal restrictions (including but not limited to trade restrictions).
      Such commissions will be cancelled and will not be compensated in any
      other manner.
    </Typography>
    <Typography variant="body1" component="p">
      The recipient of any commission is responsible for their respective tax
      obligations.
    </Typography>
    <Typography variant="h4">
      8. No employment or agency relationship
    </Typography>
    <Typography variant="body1" component="p">
      Participation in the Program does not create any employment, agency,
      partnership, or joint venture relationship. Referrers have no authority to
      bind the Company or its Affiliates. Any participant is responsible for all
      expenses incurred in connection with this program.
    </Typography>
    <Typography variant="h4">9. Privacy and personal data</Typography>
    <Typography variant="body1" component="p">
      The Company and its Affiliates collect and process personal data provided
      by Referrers and candidates solely for Program administration,
      recruitment, and legal compliance purposes. By submitting a referral, the
      Referrer confirms that the candidate has provided valid consent. Personal
      data may be processed in the United States or other jurisdictions.
    </Typography>
    <Typography variant="h4">10. Governing law and jurisdiction</Typography>
    <Typography variant="body1" component="p">
      These Terms shall be governed by the laws of the State of Florida. Any
      disputes shall be resolved exclusively in the state or federal courts
      located in Florida.
    </Typography>
    <Typography variant="h4">11. Amendments and termination</Typography>
    <Typography variant="body1" component="p">
      The Company reserves the right to amend, suspend, or terminate the Program
      at any time. Amendments shall apply prospectively only.
    </Typography>
    <Typography variant="h4">12. Entire agreement</Typography>
    <Typography variant="body1" component="p">
      These Terms constitute the entire agreement regarding the Program and
      supersede all prior communications.
    </Typography>
    <Typography variant="body1" component="p">
      IN WITNESS WHEREOF, participation in the Program constitutes acceptance of
      these Terms.
    </Typography>
  </Stack>
);
